import { useCallback, useEffect, useState } from "react";

import type { NurseryItemDto } from "@/generated/model";
import { listNurseriesQueryControllerListNurseries } from "@/generated/nurseries/nurseries";
import { offlineDb, patchNurseryContextRow } from "@/lib/offline/db";

interface UseOfflineNurseryDataResult {
  nurseries: NurseryItemDto[];
  nurseriesByOrg: Record<string, NurseryItemDto[]>;
  isLoading: boolean;
  isOffline: boolean;
  refresh: () => Promise<void>;
}

const CACHE_KEY = "nursery-context";

/**
 * Offline-first hook for fetching nurseries.
 * Fetches ALL nurseries the user has access to (backend handles access control via ZModel).
 * Groups nurseries by organisationId for easy access.
 *
 * @param enabled - Whether to enable fetching
 */
export function useOfflineNurseryData(
  enabled: boolean,
): UseOfflineNurseryDataResult {
  const [nurseries, setNurseries] = useState<NurseryItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Compute nurseries grouped by organisation
  const nurseriesByOrg = useCallback(() => {
    return nurseries.reduce<Record<string, NurseryItemDto[]>>(
      (acc, nursery) => {
        const orgId = nursery.organisationId;
        if (!acc[orgId]) {
          acc[orgId] = [];
        }
        acc[orgId].push(nursery);
        return acc;
      },
      {},
    );
  }, [nurseries])();

  // Load cached nurseries from IndexedDB
  const loadFromCache = useCallback(async (): Promise<NurseryItemDto[]> => {
    try {
      const cached = await offlineDb.nurseryContext.get(CACHE_KEY);
      if (cached?.orgNurseries) {
        return cached.orgNurseries as NurseryItemDto[];
      }
    } catch (err) {
      console.error("Failed to load nurseries from cache:", err);
    }
    return [];
  }, []);

  // Save nurseries to IndexedDB cache
  const saveToCache = useCallback(async (nurseriesList: NurseryItemDto[]) => {
    try {
      await patchNurseryContextRow(CACHE_KEY, {
        orgNurseries: nurseriesList as unknown,
      });
    } catch (err) {
      console.error("Failed to save nurseries to cache:", err);
    }
  }, []);

  // Fetch ALL nurseries the user has access to
  const fetchNurseries = useCallback(async (): Promise<NurseryItemDto[]> => {
    try {
      const response = await listNurseriesQueryControllerListNurseries({
        limit: 1000,
      });
      return (response?.data?.data || []) as NurseryItemDto[];
    } catch (err) {
      console.error("Failed to fetch nurseries:", err);
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const freshNurseries = await fetchNurseries();
      setNurseries(freshNurseries);
      await saveToCache(freshNurseries);
    } catch (err) {
      console.error("Failed to refresh nurseries:", err);
    }
  }, [fetchNurseries, saveToCache]);

  // Initial load effect
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function initialize() {
      setIsLoading(true);

      // Step 1: Load cached nurseries immediately
      const cachedNurseries = await loadFromCache();
      if (cachedNurseries.length > 0 && isMounted) {
        setNurseries(cachedNurseries);
      }

      // Step 2: If online, fetch fresh data
      if (navigator.onLine) {
        try {
          const freshNurseries = await fetchNurseries();

          if (isMounted && freshNurseries.length > 0) {
            setNurseries(freshNurseries);
            await saveToCache(freshNurseries);
          }
        } catch (err) {
          // If fetch fails, we already have cached data (if any)
          console.error("Online fetch failed, using cached data:", err);
        }
      }

      if (isMounted) {
        setIsLoading(false);
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, [enabled, loadFromCache, fetchNurseries, saveToCache]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    nurseries,
    nurseriesByOrg,
    isLoading,
    isOffline,
    refresh,
  };
}

/**
 * Save the selected nursery to cache for offline use.
 */
export async function cacheSelectedNursery(
  nurseryId: string | null,
  orgId: string | null,
): Promise<void> {
  try {
    await patchNurseryContextRow(CACHE_KEY, {
      selectedNurseryId: nurseryId,
      activeOrgId: orgId,
    });
  } catch (err) {
    console.error("Failed to cache selected nursery:", err);
  }
}

/**
 * Get the cached selected nursery for offline use.
 */
export async function getCachedSelectedNursery(): Promise<{
  nurseryId: string | null;
  orgId: string | null;
} | null> {
  try {
    const cached = await offlineDb.nurseryContext.get(CACHE_KEY);
    if (cached) {
      return {
        nurseryId: cached.selectedNurseryId,
        orgId: cached.activeOrgId,
      };
    }
  } catch (err) {
    console.error("Failed to get cached selected nursery:", err);
  }
  return null;
}
